import { useState, useEffect, useCallback, useRef } from "react";

import {
  listAlarmUsers, listAlarms, createAlarm, updateAlarm,
  deleteAlarm, listGroups, listUsers, fetchTagsByIds,
} from "../../../../api";
import { useNotify } from "../../../../ToastContext";
import { usePermission } from "../../../../UserContext";
import Select from "../../../../components/Select";
import TagPicker from "../../../../components/TagPicker";
import NumberInput from "../../../../components/NumberInput";
import searchIconSrc from "../../../../assets/search.svg";
import xmarkIcon from "../../../../assets/xmark.svg";
import plusIcon from "../../../../assets/plus.svg";

// ── Constants ────────────────────────────────────────────────────────

const MODE_OPTIONS = [
  { value: "above", label: "Above" },
  { value: "below", label: "Below" },
  { value: "between", label: "Between" },
  { value: "outside_range", label: "Outside Range" },
];

const SEVERITY_OPTIONS = [
  { value: "critical", label: "Critical" },
  { value: "high", label: "High" },
  { value: "medium", label: "Medium" },
  { value: "low", label: "Low" },
];

const SEVERITY_COLORS = {
  critical: { bg: "rgba(244,67,54,0.12)", color: "#f44336", border: "rgba(244,67,54,0.25)" },
  high: { bg: "rgba(255,152,0,0.12)", color: "#e68900", border: "rgba(255,152,0,0.25)" },
  medium: { bg: "rgba(200,150,0,0.12)", color: "#b08800", border: "rgba(200,150,0,0.25)" },
  low: { bg: "rgba(80,140,255,0.12)", color: "rgba(60,120,220,0.95)", border: "rgba(80,140,255,0.25)" },
};

const EMPTY_ALARM = {
  tag_id: "", name: "", mode: "above", setpoint: "", setpoint_high: "",
  message: "", severity: "medium", on_delay_ms: 0, off_delay_ms: 0,
  notify_all: false, notify_user_ids: [], notify_group_ids: [],
};

const modeLabel = (mode) => MODE_OPTIONS.find((m) => m.value === mode)?.label || mode;
const needsHigh = (mode) => mode === "between" || mode === "outside_range";

// ── Small Components ─────────────────────────────────────────────────

const SeverityBadge = ({ severity }) => {
  const c = SEVERITY_COLORS[severity] || SEVERITY_COLORS.medium;
  return (
    <span style={{
      fontSize: 10, textTransform: "capitalize", padding: "2px 7px", borderRadius: 5,
      background: c.bg, color: c.color, border: `1px solid ${c.border}`,
      fontWeight: 600, whiteSpace: "nowrap",
    }}>{severity}</span>
  );
};

const StatusDot = ({ enabled }) => (
  <div style={{
    width: 8, height: 8, borderRadius: "50%", flexShrink: 0,
    background: enabled ? "#4caf50" : "var(--gw-muted)",
  }} title={enabled ? "Enabled" : "Disabled"} />
);

const AlarmSummary = ({ alarm, getTagPath }) => (
  <div style={{ fontSize: 11, color: "var(--gw-muted)" }}>
    {`${getTagPath(alarm.tag_id)} \u00b7 ${modeLabel(alarm.mode)} ${alarm.setpoint}${alarm.setpoint_high != null ? `\u2013${alarm.setpoint_high}` : ""}`}
  </div>
);

// ── Notification Selector ────────────────────────────────────────────

const NotifySelector = ({ users, groups, notifyAll, setNotifyAll, userIds, toggleUser, groupIds, toggleGroup }) => {
  const [tab, setTab] = useState("users");
  const [search, setSearch] = useState("");
  const q = search.toLowerCase();

  const filteredUsers = q
    ? users.filter((u) => u.username?.toLowerCase().includes(q) || u.email?.toLowerCase().includes(q) || u.first_name?.toLowerCase().includes(q) || u.last_name?.toLowerCase().includes(q))
    : users;
  const filteredGroups = q ? groups.filter((g) => g.name?.toLowerCase().includes(q)) : groups;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
      <label style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer", fontSize: 13 }}>
        <input type="checkbox" checked={notifyAll} onChange={(e) => setNotifyAll(e.target.checked)} style={{ width: 14, height: 14, accentColor: "var(--gw-accent)", cursor: "pointer", flexShrink: 0 }} />
        Notify all users with email
      </label>

      {!notifyAll && (
        <div style={{ border: "1px solid var(--gw-border)", borderRadius: 10, overflow: "hidden" }}>
          <div style={{ display: "flex", borderBottom: "1px solid var(--gw-border)" }}>
            {[{ key: "users", label: "Users", count: userIds.length }, { key: "groups", label: "Groups", count: groupIds.length }].map((t) => (
              <button key={t.key} type="button" onClick={() => { setTab(t.key); setSearch(""); }}
                style={{
                  flex: 1, padding: "8px 12px", fontSize: 12, fontWeight: 500, border: "none", cursor: "pointer",
                  background: tab === t.key ? "var(--gw-surface-1)" : "transparent",
                  color: tab === t.key ? "var(--gw-text)" : "var(--gw-muted)",
                  borderBottom: tab === t.key ? "2px solid var(--gw-accent)" : "2px solid transparent",
                }}>
                {t.label} {t.count > 0 && `(${t.count})`}
              </button>
            ))}
          </div>

          <div style={{ padding: "8px 8px 0" }}>
            <div className="gwSearch" style={{ width: "100%", height: 32 }}>
              <img src={searchIconSrc} alt="" className="gwSearchIcon" />
              <input placeholder={tab === "users" ? "Search users..." : "Search groups..."} value={search} onChange={(e) => setSearch(e.target.value)} style={{ fontSize: 12 }} />
            </div>
          </div>

          <div style={{ maxHeight: 180, overflowY: "auto", padding: "6px 4px" }}>
            {tab === "users" && filteredUsers.map((u) => {
              const checked = userIds.includes(u.user_id);
              const name = [u.first_name, u.last_name].filter(Boolean).join(" ");
              return (
                <label key={u.user_id} style={{ display: "flex", alignItems: "center", gap: 10, padding: "6px 8px", borderRadius: 6, cursor: "pointer", fontSize: 13, background: checked ? "var(--gw-surface-1)" : "transparent" }}>
                  <input type="checkbox" checked={checked} onChange={() => toggleUser(u.user_id)} style={{ width: 14, height: 14, accentColor: "var(--gw-accent)", cursor: "pointer", flexShrink: 0 }} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: 500 }}>{u.username}{name && <span style={{ fontWeight: 400, color: "var(--gw-text-secondary)", marginLeft: 6 }}>{name}</span>}</div>
                    {u.email && <div style={{ fontSize: 11, color: "var(--gw-muted)" }}>{u.email}</div>}
                  </div>
                </label>
              );
            })}
            {tab === "users" && filteredUsers.length === 0 && <div style={{ padding: 12, textAlign: "center", color: "var(--gw-muted)", fontSize: 12 }}>{search ? "No users match." : "No users with email."}</div>}

            {tab === "groups" && filteredGroups.map((g) => {
              const checked = groupIds.includes(g.id);
              return (
                <label key={g.id} style={{ display: "flex", alignItems: "center", gap: 10, padding: "6px 8px", borderRadius: 6, cursor: "pointer", fontSize: 13, background: checked ? "var(--gw-surface-1)" : "transparent" }}>
                  <input type="checkbox" checked={checked} onChange={() => toggleGroup(g.id)} style={{ width: 14, height: 14, accentColor: "var(--gw-accent)", cursor: "pointer", flexShrink: 0 }} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: 500 }}>{g.name}</div>
                    <div style={{ fontSize: 11, color: "var(--gw-muted)" }}>{(g.userIds || []).length} members</div>
                  </div>
                </label>
              );
            })}
            {tab === "groups" && filteredGroups.length === 0 && <div style={{ padding: 12, textAlign: "center", color: "var(--gw-muted)", fontSize: 12 }}>{search ? "No groups match." : "No groups created."}</div>}
          </div>
        </div>
      )}
    </div>
  );
};

// ── Alarm Form Fields (shared between create + edit) ─────────────────

const AlarmFields = ({ alarm, setField, users, groups, toggleUser, toggleGroup }) => (
  <>
    <div className="gwField">
      <label className="gwLabel">Name</label>
      <input className="gwInput" placeholder="e.g. High Temperature" value={alarm.name || ""} onChange={(e) => setField("name", e.target.value)} />
    </div>

    <div className="gwFieldRow">
      <div className="gwField">
        <label className="gwLabel">Mode</label>
        <Select value={alarm.mode} onChange={(v) => setField("mode", v)} options={MODE_OPTIONS} />
      </div>
      <div className="gwField">
        <label className="gwLabel">Severity</label>
        <Select value={alarm.severity || "medium"} onChange={(v) => setField("severity", v)} options={SEVERITY_OPTIONS} />
      </div>
    </div>

    <div className="gwFieldRow">
      <div className="gwField">
        <label className="gwLabel">Setpoint</label>
        <input className="gwInput" type="number" placeholder="100" value={alarm.setpoint} onChange={(e) => setField("setpoint", e.target.value)} />
      </div>
      {needsHigh(alarm.mode) && (
        <div className="gwField">
          <label className="gwLabel">Setpoint High</label>
          <input className="gwInput" type="number" placeholder="200" value={alarm.setpoint_high || ""} onChange={(e) => setField("setpoint_high", e.target.value)} />
        </div>
      )}
    </div>

    <div className="gwField">
      <label className="gwLabel">Message</label>
      <textarea className="gwInput" rows={2} placeholder="Temperature exceeded limit" value={alarm.message} onChange={(e) => setField("message", e.target.value)} style={{ resize: "vertical" }} />
    </div>

    <div className="gwFieldRow">
      <div className="gwField">
        <label className="gwLabel">Activation Delay (ms)</label>
        <NumberInput value={String(alarm.on_delay_ms || 0)} onChange={(e) => setField("on_delay_ms", parseInt(e.target.value) || 0)} min={0} />
      </div>
      <div className="gwField">
        <label className="gwLabel">Clear Delay (ms)</label>
        <NumberInput value={String(alarm.off_delay_ms || 0)} onChange={(e) => setField("off_delay_ms", parseInt(e.target.value) || 0)} min={0} />
      </div>
    </div>

    <div style={{ borderTop: "1px solid var(--gw-border)", paddingTop: 12, marginTop: 4 }}>
      <div style={{ fontSize: 11, color: "var(--gw-muted)", textTransform: "uppercase", letterSpacing: 0.8, fontWeight: 600, marginBottom: 10 }}>
        Notification Targets
      </div>
      <NotifySelector
        users={users} groups={groups}
        notifyAll={alarm.notify_all} setNotifyAll={(v) => setField("notify_all", v)}
        userIds={alarm.notify_user_ids || []} toggleUser={toggleUser}
        groupIds={alarm.notify_group_ids || []} toggleGroup={toggleGroup}
      />
    </div>
  </>
);

// ── Expanded Alarm List (used in recipients section) ─────────────────

const ExpandedAlarmList = ({ alarms, getTagPath, onEdit }) => (
  <div style={{ display: "flex", flexDirection: "column", gap: 6, padding: "8px 8px 8px" }}>
    {alarms.map((a) => (
      <div key={a.alarm_id}
        data-gw-resource="alarm_rules" data-gw-id={a.alarm_id} data-gw-name={a.name || a.message}
        style={{
        display: "flex", alignItems: "center", gap: 12, padding: "8px 12px",
        borderRadius: 8, border: "1px solid var(--gw-border)",
        background: a.enabled ? "var(--gw-surface-1)" : "transparent",
        opacity: a.enabled ? 1 : 0.55,
      }}>
        <StatusDot enabled={a.enabled} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, minWidth: 0 }}>
            <span style={{ fontSize: 13, fontWeight: 500, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", minWidth: 0 }}>{a.name || a.message}</span>
            <SeverityBadge severity={a.severity || "medium"} />
          </div>
          <AlarmSummary alarm={a} getTagPath={getTagPath} />
        </div>
        <button className="gwGhostBtn" type="button"
          onClick={(e) => { e.stopPropagation(); onEdit(a); }}
          style={{ height: 26, fontSize: 11, padding: "0 8px", flexShrink: 0 }}>Edit</button>
      </div>
    ))}
  </div>
);

// ── Main Page ────────────────────────────────────────────────────────

const AlarmNotificationsPage = () => {
  const canConfigure = usePermission("configure");
  const [loading, setLoading] = useState(true);
  const [users, setUsers] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [groups, setGroups] = useState([]);
  const [alarms, setAlarms] = useState([]);
  const [tagMap, setTagMap] = useState({});
  const [searchQuery, setSearchQuery] = useState("");
  const [editingAlarm, setEditingAlarm] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [expandedRecipient, setExpandedRecipient] = useState(null);
  const [newAlarm, setNewAlarm] = useState(EMPTY_ALARM);
  const [creating, setCreating] = useState(false);
  const notify = useNotify();
  const loadGeneration = useRef(0);

  // ── Data Loading ────────────────────────────────────────────────

  const loadData = useCallback(async () => {
    const generation = ++loadGeneration.current;
    try {
      const [userData, alarmData, groupData, allUserData] = await Promise.all([
        listAlarmUsers(), listAlarms(),
        listGroups().catch(() => []),
        listUsers().catch(() => ({ users: [] })),
      ]);

      const rawAlarms = Array.isArray(alarmData) ? alarmData : (alarmData?.alarms || []);
      const rawGroups = Array.isArray(groupData) ? groupData : (groupData?.groups || []);
      const map = {};
      const referencedTags = await fetchTagsByIds(rawAlarms.map((alarm) => alarm.tag_id));
      for (const t of referencedTags) {
        const id = String(t.tagId || t.id || t.tag_id || "");
        const path = t.fullPath || t.full_path || t.path || t.name || "";
        if (id && path) map[id] = path;
      }
      if (generation !== loadGeneration.current) return;
      setUsers(userData?.users || []);
      setAllUsers(allUserData?.users || []);
      setAlarms(rawAlarms);
      setGroups(rawGroups.map((g) => ({ ...g, userIds: g.member_ids || [] })));
      setTagMap(map);
    } catch (err) {
      if (generation === loadGeneration.current) {
        notify({ type: "error", title: "Error", message: err.message || "Failed to load data" });
      }
    } finally {
      if (generation === loadGeneration.current) setLoading(false);
    }
  }, [notify]);

  useEffect(() => {
    loadData();
    return () => { loadGeneration.current += 1; };
  }, [loadData]);

  // Refresh when the AI mutates an alarm rule.
  useEffect(() => {
    const handler = (e) => {
      if (e.detail?.resource === "alarm_rules") loadData();
    };
    window.addEventListener("gw-ai-resource-changed", handler);
    return () => window.removeEventListener("gw-ai-resource-changed", handler);
  }, [loadData]);

  // AI propose-alarm: open the New Alarm Rule modal pre-filled.
  useEffect(() => {
    const handler = (e) => {
      const payload = e.detail?.payload;
      if (!payload || typeof payload !== "object") return;
      const next = { ...EMPTY_ALARM };
      for (const key of Object.keys(EMPTY_ALARM)) {
        if (payload[key] != null) next[key] = payload[key];
      }
      // setpoint may arrive as a number — the form's numeric input expects a string.
      if (next.setpoint != null && typeof next.setpoint !== "string") {
        next.setpoint = String(next.setpoint);
      }
      if (next.setpoint_high != null && typeof next.setpoint_high !== "string") {
        next.setpoint_high = String(next.setpoint_high);
      }
      setNewAlarm(next);
      setShowCreate(true);
    };
    window.addEventListener("gw-ai-propose-alarm-page", handler);
    return () => window.removeEventListener("gw-ai-propose-alarm-page", handler);
  }, []);

  // ── Helpers ─────────────────────────────────────────────────────

  const getTagPath = (id) => tagMap[String(id)] || "Tag unavailable";
  const getUserName = (id) => users.find((u) => u.user_id === id)?.username || id;
  const getGroupName = (id) => groups.find((g) => g.id === id)?.name || id;

  const setNewField = (k, v) => setNewAlarm((p) => ({ ...p, [k]: v }));
  const setEditField = (k, v) => setEditingAlarm((p) => ({ ...p, [k]: v }));

  const toggleListItem = (setter, field, id) => {
    setter((a) => {
      const ids = a[field] || [];
      return { ...a, [field]: ids.includes(id) ? ids.filter((x) => x !== id) : [...ids, id] };
    });
  };

  const filtered = alarms.filter((a) => {
    if (!searchQuery) return true;
    const q = searchQuery.toLowerCase();
    return a.name?.toLowerCase().includes(q) || getTagPath(a.tag_id).toLowerCase().includes(q) || a.message?.toLowerCase().includes(q)
      || a.mode?.toLowerCase().includes(q) || a.severity?.toLowerCase().includes(q);
  });

  // ── CRUD ────────────────────────────────────────────────────────

  const handleCreate = async () => {
    if (!newAlarm.tag_id || !newAlarm.message) {
      notify({ type: "error", title: "Error", message: "Tag and message are required." });
      return;
    }
    setCreating(true);
    try {
      await createAlarm(newAlarm.tag_id, {
        mode: newAlarm.mode,
        setpoint: parseFloat(newAlarm.setpoint) || 0,
        setpoint_high: needsHigh(newAlarm.mode) ? parseFloat(newAlarm.setpoint_high) || 0 : undefined,
        message: newAlarm.message,
        severity: newAlarm.severity,
        on_delay_ms: newAlarm.on_delay_ms || 0,
        off_delay_ms: newAlarm.off_delay_ms || 0,
        notify_all: newAlarm.notify_all,
        notify_user_ids: newAlarm.notify_user_ids,
        notify_group_ids: newAlarm.notify_group_ids,
        enabled: true,
      });
      notify({ type: "success", title: "Created", message: "Alarm created." });
      setShowCreate(false);
      setNewAlarm(EMPTY_ALARM);
      await loadData();
    } catch (err) {
      notify({ type: "error", title: "Error", message: err.message || "Failed to create alarm" });
    } finally {
      setCreating(false);
    }
  };

  const handleSave = async () => {
    if (!editingAlarm) return;
    try {
      await updateAlarm(editingAlarm.alarm_id, editingAlarm);
      notify({ type: "success", title: "Saved", message: "Alarm updated." });
      setEditingAlarm(null);
      await loadData();
    } catch (err) {
      notify({ type: "error", title: "Error", message: err.message || "Failed to update" });
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await deleteAlarm(deleteTarget.alarm_id);
      notify({ type: "success", title: "Deleted", message: "Alarm deleted." });
      setDeleteTarget(null);
      setEditingAlarm(null);
      await loadData();
    } catch (err) {
      notify({ type: "error", title: "Error", message: err.message || "Failed to delete" });
    } finally {
      setDeleting(false);
    }
  };

  // ── Loading State ───────────────────────────────────────────────

  if (loading) {
    return (
      <div className="gwContentGrid">
        <section className="gwFormStack">
          <div className="gwPageHeader">
            <div>
              <div className="gwPageTitle">Alarm Notifications</div>
              <div className="gwPageSubtitle">Loading...</div>
            </div>
          </div>
        </section>
      </div>
    );
  }

  // ── Computed ────────────────────────────────────────────────────

  const enabledCount = alarms.filter((a) => a.enabled).length;

  const usersWithAlarms = users.map((u) => ({
    ...u,
    alarmList: alarms.filter((a) => a.notify_all || (a.notify_user_ids || []).includes(u.user_id)),
  })).filter((u) => u.alarmList.length > 0);

  const groupsWithAlarms = groups.map((g) => ({
    ...g,
    alarmList: alarms.filter((a) => (a.notify_group_ids || []).includes(g.id)),
  })).filter((g) => g.alarmList.length > 0);

  const totalRecipients = usersWithAlarms.length + groupsWithAlarms.length;

  // ── Render ──────────────────────────────────────────────────────

  return (
    <div className="gwContentGrid">
      <section className="gwFormStack">

        {/* Header */}
        <div className="gwPageHeader">
          <div>
            <div className="gwPageTitle">Alarm Notifications</div>
            <div className="gwPageSubtitle">
              {`${alarms.length} alarm${alarms.length !== 1 ? "s" : ""} \u00b7 ${enabledCount} active \u00b7 ${users.length} recipient${users.length !== 1 ? "s" : ""}`}
            </div>
          </div>
          <button className="gwPrimaryBtn" type="button" onClick={() => { setShowCreate(true); setNewAlarm(EMPTY_ALARM); }}
            disabled={!canConfigure}
            title={canConfigure ? undefined : "Requires configure permission"}
            style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
            <img src={plusIcon} alt="" className="gwIcon" style={{ width: 14, height: 14 }} /><span>Add Alarm</span>
          </button>
        </div>

        {/* Create Modal */}
        {showCreate && (
          <div className="gwOverlay" onClick={() => setShowCreate(false)}>
            <div className="gwModal" onClick={(e) => e.stopPropagation()} style={{ width: 540 }}>
              <div className="gwModalHeader">
                <div>
                  <div className="gwModalTitle">New Alarm</div>
                  <div className="gwModalSubtitle">Configure a new alarm notification</div>
                </div>
                <button className="gwModalClose" type="button" onClick={() => setShowCreate(false)}>
                  <img src={xmarkIcon} alt="Close" className="gwIcon" style={{ width: 10, height: 10, opacity: 0.7 }} />
                </button>
              </div>
              <div className="gwModalBody">
                <div className="gwField">
                  <label className="gwLabel">Tag</label>
                  <TagPicker value={newAlarm.tag_id} displayValue={newAlarm.tag_id ? getTagPath(newAlarm.tag_id) : ""}
                    onChange={(id, path) => {
                      setNewField("tag_id", id);
                      if (id && path) setTagMap((current) => ({ ...current, [String(id)]: path }));
                    }} placeholder="Search or browse tags…" />
                </div>
                <AlarmFields
                  alarm={newAlarm} setField={setNewField} users={users} groups={groups}
                  toggleUser={(id) => toggleListItem(setNewAlarm, "notify_user_ids", id)}
                  toggleGroup={(id) => toggleListItem(setNewAlarm, "notify_group_ids", id)}
                />
              </div>
              <div className="gwModalFooter" style={{ justifyContent: "space-between" }}>
                <div style={{ display: "flex", gap: 8 }}>
                  <button className="gwPrimaryBtn" type="button" onClick={handleCreate}
                    disabled={creating || !newAlarm.tag_id || !newAlarm.message || !newAlarm.setpoint}>
                    {creating ? "Creating..." : "Create Alarm"}
                  </button>
                  <button className="gwGhostBtn" type="button" onClick={() => setShowCreate(false)}>Cancel</button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Alarm List */}
        <div className="gwCardSection">
          <div className="gwSectionTitle" style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <span>Alarms</span>
            {alarms.length > 3 && (
              <div className="gwSearch" style={{ width: 200, height: 30 }}>
                <img src={searchIconSrc} alt="" className="gwSearchIcon" />
                <input placeholder="Search..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} style={{ fontSize: 12 }} />
              </div>
            )}
          </div>
          <div className="gwCardSectionBody" style={{ padding: 0 }}>
            {filtered.length === 0 && (
              <div style={{ padding: 24, textAlign: "center", color: "var(--gw-muted)", fontSize: 13 }}>
                {searchQuery ? "No alarms match." : "No alarms configured. Click 'Add Alarm' above."}
              </div>
            )}
            {filtered.map((a) => {
              const targets = [];
              if (a.notify_all) targets.push("All users");
              (a.notify_user_ids || []).forEach((id) => targets.push(getUserName(id)));
              (a.notify_group_ids || []).forEach((id) => targets.push(getGroupName(id)));

              return (
                <div key={a.alarm_id} className="umUserRow"
                  data-gw-resource="alarm_rules" data-gw-id={a.alarm_id} data-gw-name={a.name || a.message}
                  style={{ cursor: "pointer", opacity: a.enabled ? 1 : 0.6 }}
                  onClick={() => setEditingAlarm({ ...a })}>
                  <StatusDot enabled={a.enabled} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 8, minWidth: 0 }}>
                      <span className="umUserName" style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", minWidth: 0 }}>{a.name || a.message}</span>
                      <SeverityBadge severity={a.severity || "medium"} />
                    </div>
                    <div style={{ fontSize: 11, color: "var(--gw-muted)" }}>
                      {`${getTagPath(a.tag_id)} \u00b7 ${modeLabel(a.mode)} ${a.setpoint}${a.setpoint_high != null ? `\u2013${a.setpoint_high}` : ""}`}
                    </div>
                    {targets.length > 0 && (
                      <div style={{ display: "flex", gap: 4, flexWrap: "wrap", marginTop: 4 }}>
                        {targets.map((t, i) => (
                          <span key={i} style={{
                            fontSize: 10, padding: "1px 6px", borderRadius: 4,
                            background: "var(--gw-surface-2)", color: "var(--gw-text-secondary)",
                            border: "1px solid var(--gw-border)",
                          }}>{t}</span>
                        ))}
                      </div>
                    )}
                  </div>
                  <button className="gwGhostBtn umEditBtn" type="button"
                    onClick={(e) => { e.stopPropagation(); setEditingAlarm({ ...a }); }}
                    style={{ fontSize: 11, flexShrink: 0 }}>Edit</button>
                </div>
              );
            })}
          </div>
        </div>

        {/* Active Recipients */}
        {totalRecipients > 0 && (
          <div className="gwCardSection">
            <div className="gwSectionTitle">{`Active Recipients (${totalRecipients})`}</div>
            <div className="gwCardSectionBody" style={{ padding: 0 }}>

              {usersWithAlarms.map((u) => {
                const fullName = [u.first_name, u.last_name].filter(Boolean).join(" ");
                const key = `user:${u.user_id}`;
                const isExpanded = expandedRecipient === key;
                return (
                  <div key={u.user_id}>
                    <div className="umUserRow" style={{ padding: "10px 16px", cursor: "pointer" }}
                      onClick={() => setExpandedRecipient(isExpanded ? null : key)}>
                      <div style={{
                        width: 30, height: 30, borderRadius: "50%", flexShrink: 0, overflow: "hidden",
                        background: u.avatar ? "transparent" : "rgba(80,140,255,0.15)",
                        border: u.avatar ? "none" : "1px solid rgba(80,140,255,0.25)",
                        display: "flex", alignItems: "center", justifyContent: "center",
                        fontSize: 12, fontWeight: 600, color: "rgba(140,180,255,0.95)",
                      }}>
                        {u.avatar
                          ? <img src={u.avatar} alt="" style={{ width: "100%", height: "100%", objectFit: "cover", borderRadius: "50%" }} />
                          : (u.username || "?")[0].toUpperCase()}
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                          <span style={{ fontSize: 13, fontWeight: 500 }}>{u.username}</span>
                          {fullName && <span style={{ fontSize: 12, color: "var(--gw-text-secondary)" }}>{fullName}</span>}
                        </div>
                        <div className="umUserMeta">{u.email}</div>
                      </div>
                      <span style={{
                        fontSize: 10, padding: "2px 7px", borderRadius: 5,
                        background: "rgba(80,140,255,0.12)", color: "rgba(120,170,255,0.95)",
                        border: "1px solid rgba(80,140,255,0.2)", fontWeight: 600,
                      }}>{u.alarmList.length} alarm{u.alarmList.length !== 1 ? "s" : ""}</span>
                    </div>
                    {isExpanded && <ExpandedAlarmList alarms={u.alarmList} getTagPath={getTagPath} onEdit={(a) => setEditingAlarm({ ...a })} />}
                  </div>
                );
              })}

              {groupsWithAlarms.map((g) => {
                const key = `group:${g.id}`;
                const isExpanded = expandedRecipient === key;
                return (
                  <div key={g.id}>
                    <div className="umUserRow" style={{ padding: "10px 16px", cursor: "pointer" }}
                      onClick={() => setExpandedRecipient(isExpanded ? null : key)}>
                      <div style={{ display: "flex", alignItems: "center", flex: 1, gap: 12, minWidth: 0 }}>
                        <div style={{ display: "flex", flexShrink: 0 }}>
                          {(() => {
                            const members = allUsers.filter((u) => (g.userIds || []).includes(u.user_id));
                            const mc = members.length;
                            if (mc === 0) return (
                              <div style={{
                                width: 28, height: 28, borderRadius: "50%", border: "1px dashed var(--gw-border-strong)",
                                display: "flex", alignItems: "center", justifyContent: "center",
                                fontSize: 12, color: "var(--gw-muted)",
                              }}>?</div>
                            );
                            return members.slice(0, 3).map((u, i) => (
                              <div key={u.user_id} style={{ marginLeft: i > 0 ? -8 : 0, zIndex: 3 - i }}>
                                <div style={{
                                  width: 28, height: 28, borderRadius: "50%", flexShrink: 0, overflow: "hidden",
                                  background: u.avatar ? "transparent" : "rgba(80,140,255,0.15)",
                                  border: u.avatar ? "none" : "1px solid rgba(80,140,255,0.25)",
                                  display: "flex", alignItems: "center", justifyContent: "center",
                                  fontSize: 11.2, fontWeight: 600, color: "rgba(140,180,255,0.95)",
                                }}>
                                  {u.avatar
                                    ? <img src={u.avatar} alt="" style={{ width: "100%", height: "100%", objectFit: "cover", borderRadius: "50%" }} />
                                    : (u.username || "?")[0].toUpperCase()}
                                </div>
                              </div>
                            ));
                          })()}
                        </div>
                        <div style={{ minWidth: 0 }}>
                          <span style={{ fontSize: 13, fontWeight: 500 }}>{g.name}</span>
                          <div className="umUserMeta">{(g.userIds || []).length} member{(g.userIds || []).length !== 1 ? "s" : ""}</div>
                        </div>
                      </div>
                      <span style={{
                        fontSize: 10, padding: "2px 7px", borderRadius: 5,
                        background: "rgba(255,193,7,0.12)", color: "rgba(255,193,7,0.95)",
                        border: "1px solid rgba(255,193,7,0.2)", fontWeight: 600,
                      }}>{g.alarmList.length} alarm{g.alarmList.length !== 1 ? "s" : ""}</span>
                    </div>
                    {isExpanded && <ExpandedAlarmList alarms={g.alarmList} getTagPath={getTagPath} onEdit={(a) => setEditingAlarm({ ...a })} />}
                  </div>
                );
              })}

            </div>
          </div>
        )}

      </section>

      {/* Edit Modal */}
      {editingAlarm && (
        <div className="gwOverlay" onClick={() => setEditingAlarm(null)}>
          <div className="gwModal" onClick={(e) => e.stopPropagation()} style={{ width: 540 }}>
            <div className="gwModalHeader">
              <div>
                <div className="gwModalTitle">Edit Alarm</div>
                <div className="gwModalSubtitle">{`${getTagPath(editingAlarm.tag_id)} \u00b7 ${modeLabel(editingAlarm.mode)}`}</div>
              </div>
              <button className="gwModalClose" type="button" onClick={() => setEditingAlarm(null)}>
                <img src={xmarkIcon} alt="Close" className="gwIcon" style={{ width: 10, height: 10, opacity: 0.7 }} />
              </button>
            </div>
            <div className="gwModalBody">
              <AlarmFields
                alarm={editingAlarm} setField={setEditField} users={users} groups={groups}
                toggleUser={(id) => toggleListItem(setEditingAlarm, "notify_user_ids", id)}
                toggleGroup={(id) => toggleListItem(setEditingAlarm, "notify_group_ids", id)}
              />
              <label style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer", fontSize: 13 }}>
                <input type="checkbox" checked={editingAlarm.enabled}
                  onChange={(e) => setEditField("enabled", e.target.checked)}
                  style={{ accentColor: "var(--gw-accent)" }} />
                Enabled
              </label>
            </div>
            <div className="gwModalFooter" style={{ justifyContent: "space-between" }}>
              <div style={{ display: "flex", gap: 8 }}>
                <button className="gwPrimaryBtn" type="button" onClick={handleSave}
                  disabled={!canConfigure}
                  title={canConfigure ? undefined : "Requires configure permission"}>
                  Save
                </button>
                <button className="gwGhostBtn" type="button" onClick={() => setEditingAlarm(null)}>Cancel</button>
              </div>
              <button className="gwDangerBtn" type="button" onClick={() => setDeleteTarget(editingAlarm)}
                disabled={!canConfigure}
                title={canConfigure ? undefined : "Requires configure permission"}
                style={{ fontSize: 12 }}>
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation */}
      {deleteTarget && (
        <div className="gwOverlay" onClick={() => setDeleteTarget(null)} style={{ zIndex: 110 }}>
          <div className="gwModal" onClick={(e) => e.stopPropagation()} style={{ width: 400 }}>
            <div className="gwModalHeader">
              <div>
                <div className="gwModalTitle">Delete Alarm</div>
                <div className="gwModalSubtitle">This cannot be undone.</div>
              </div>
              <button className="gwModalClose" type="button" onClick={() => setDeleteTarget(null)}>
                <img src={xmarkIcon} alt="Close" className="gwIcon" style={{ width: 10, height: 10, opacity: 0.7 }} />
              </button>
            </div>
            <div className="gwModalBody">
              <p style={{ fontSize: 13, color: "var(--gw-text-secondary)", margin: 0 }}>
                Delete <strong>"{deleteTarget.message}"</strong> on <strong>{getTagPath(deleteTarget.tag_id)}</strong>?
              </p>
            </div>
            <div className="gwModalFooter" style={{ justifyContent: "space-between" }}>
              <button className="gwGhostBtn" type="button" onClick={() => setDeleteTarget(null)}>Cancel</button>
              <button className="gwDangerBtn" type="button" onClick={confirmDelete} disabled={deleting}>
                {deleting ? "Deleting..." : "Delete Alarm"}
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export { AlarmNotificationsPage };
